// src/lib/reconciliation.ts
// ---------------------------------------------------------------------------
// Background reconciliation jobs — STABILIZATION PHASE.
//
// These jobs run on a schedule from the resilience monitor. They are
// permanently observation-only. They never mutate family_graph_members.
// Any correction requires a separate reviewed Governor repair execution.
//
// Each job returns a structured `ReconciliationReport` so the diagnostic
// panel (window.__kcReconcile) and the system status page can show a
// human-readable summary.
//
// Reconciliation surface (per spec):
//   • one-sided spouse links
//   • duplicate members
//   • orphaned nodes (parent_ids point at deleted rows)
//   • stale cached placements (kc:layout:* / kc:tree:* localStorage keys)
//
// IMPORTANT: this file deliberately does NOT touch parent/child traversal,
// renderer logic, or any spouse-placement heuristic. Per the freeze rule,
// it only observes and (when explicitly asked) repairs symmetric bookkeeping.
// ---------------------------------------------------------------------------

import { supabase } from './supabase';
import { logError } from './errorLogger';

export interface ReconciliationReport {
  job: string;
  ranAt: string;
  ok: boolean;
  scanned: number;
  flagged: number;
  repaired: number;
  details: Array<Record<string, unknown>>;
  error?: string;
}

interface MemberRow {
  id: string;
  owner_user_id: string;
  name: string | null;
  spouse_id: string | null;
  parent_ids: string[] | null;
  created_at: string | null;
}

async function loadOwnerMembers(ownerId: string): Promise<MemberRow[]> {
  const { data, error } = await supabase
    .from('family_graph_members')
    .select('id, owner_user_id, name, spouse_id, parent_ids, created_at')
    .eq('owner_user_id', ownerId)
    .is('deleted_at', null);
  if (error) {
    logError({
      endpoint: 'family_graph_members.select',
      status: null,
      bodyPreview: error.message,
      functionName: null,
      attempt: 1,
      kind: 'db',
      message: `Reconciliation load failed: ${error.message}`,
    });
    return [];
  }
  return (data ?? []) as MemberRow[];
}

// ─── 1. One-sided spouse links ─────────────────────────────────────────────
// Definition: A.spouse_id = B but B.spouse_id ≠ A.
// Repair is never executed from reconciliation. A one-sided spouse link
// is reported for explicit reviewed Governor repair.
export async function reconcileOneSidedSpouse(
  ownerId: string,
  requestedApply = false,
): Promise<ReconciliationReport> {
  const report: ReconciliationReport = {
    job: 'one-sided-spouse',
    ranAt: new Date().toISOString(),
    ok: true,
    scanned: 0,
    flagged: 0,
    repaired: 0,
    details: [],
  };
  if (!ownerId) {
    report.ok = false;
    report.error = 'no owner';
    return report;
  }
  const members = await loadOwnerMembers(ownerId);
  report.scanned = members.length;
  const byId = new Map(members.map((m) => [m.id, m]));

  for (const a of members) {
    if (!a.spouse_id) continue;
    const b = byId.get(a.spouse_id);
    if (!b) {
      // Spouse points at a deleted / non-existent row — flag separately.
      report.flagged += 1;
      report.details.push({
        kind: 'dangling-spouse-pointer',
        from: a.id,
        fromName: a.name,
        missing: a.spouse_id,
      });
      continue;
    }
    if (b.spouse_id === a.id) continue; // bidirectional, healthy
    // One-sided.
    report.flagged += 1;
    report.details.push({
      kind: 'one-sided',
      from: a.id,
      fromName: a.name,
      to: b.id,
      toName: b.name,
      bSpouseId: b.spouse_id,
    });
    if (
      requestedApply &&
      (b.spouse_id == null ||
        !byId.has(b.spouse_id))
    ) {
      report.details.push({
        kind:
          'repair-requires-governed-execution',
        memberAId:
          a.id,
        memberBId:
          b.id,
        requestedApply:
          true,
        mutationExecuted:
          false,
      });
    }
  }
  return report;
}

// ─── 2. Duplicate members ──────────────────────────────────────────────────
// Definition: same owner_user_id + normalized name. Read-only by default —
// repair is ALWAYS done by the explicit GraphDebugPanel "Repair spouse
// anchor" flow so we never auto-merge data.
export async function reconcileDuplicateMembers(
  ownerId: string,
): Promise<ReconciliationReport> {
  const report: ReconciliationReport = {
    job: 'duplicate-members',
    ranAt: new Date().toISOString(),
    ok: true,
    scanned: 0,
    flagged: 0,
    repaired: 0,
    details: [],
  };
  if (!ownerId) {
    report.ok = false;
    report.error = 'no owner';
    return report;
  }
  const members = await loadOwnerMembers(ownerId);
  report.scanned = members.length;
  const groups = new Map<string, MemberRow[]>();
  for (const m of members) {
    const key = (m.name || '').trim().toLowerCase().replace(/\s+/g, ' ');
    if (!key) continue;
    const arr = groups.get(key) ?? [];
    arr.push(m);
    groups.set(key, arr);
  }
  for (const [key, rows] of groups) {
    if (rows.length < 2) continue;
    report.flagged += rows.length - 1;
    report.details.push({
      kind: 'duplicate-set',
      name: key,
      ids: rows.map((r) => r.id),
      keepCandidate: [...rows].sort(
        (a, b) => (a.created_at ?? '').localeCompare(b.created_at ?? ''),
      )[0]?.id,
    });
  }
  return report;
}

// ─── 3. Orphaned parent pointers ───────────────────────────────────────────
// Definition: parent_ids contains a UUID with no matching member row in
// the same owner's graph. Read-only — repair would require a UI to resolve.
export async function reconcileOrphanedParents(
  ownerId: string,
): Promise<ReconciliationReport> {
  const report: ReconciliationReport = {
    job: 'orphaned-parents',
    ranAt: new Date().toISOString(),
    ok: true,
    scanned: 0,
    flagged: 0,
    repaired: 0,
    details: [],
  };
  if (!ownerId) {
    report.ok = false;
    report.error = 'no owner';
    return report;
  }
  const members = await loadOwnerMembers(ownerId);
  report.scanned = members.length;
  const ids = new Set(members.map((m) => m.id));
  for (const m of members) {
    if (!Array.isArray(m.parent_ids) || m.parent_ids.length === 0) continue;
    const missing = m.parent_ids.filter((p) => p && !ids.has(p));
    if (missing.length === 0) continue;
    report.flagged += 1;
    report.details.push({
      kind: 'orphaned-parent',
      memberId: m.id,
      memberName: m.name,
      missing,
    });
  }
  return report;
}

// ─── 4. Stale cached placements ────────────────────────────────────────────
// The renderer is intentionally cache-free (live recompute every render),
// but legacy code paths and the GraphDebugPanel sometimes write helpful
// scratch keys under kc:layout:* / kc:tree:* / kc:pyramid:* / kc:graph-snapshot.
// This job purges anything older than 24h to prevent stale state leaking
// into a future cached-renderer experiment.
export function reconcileStaleCaches(maxAgeMs = 24 * 60 * 60 * 1000): ReconciliationReport {
  const report: ReconciliationReport = {
    job: 'stale-caches',
    ranAt: new Date().toISOString(),
    ok: true,
    scanned: 0,
    flagged: 0,
    repaired: 0,
    details: [],
  };
  if (typeof window === 'undefined' || !window.localStorage) return report;
  const now = Date.now();
  const prefixes = ['kc:layout:', 'kc:tree:', 'kc:pyramid:', 'kc:graph-snapshot:'];
  const keys: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (!k) continue;
    if (prefixes.some((p) => k.startsWith(p))) keys.push(k);
  }
  report.scanned = keys.length;
  for (const k of keys) {
    try {
      const raw = localStorage.getItem(k);
      if (!raw) continue;
      let writtenAt: number | null = null;
      try {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object' && typeof parsed.writtenAt === 'number') {
          writtenAt = parsed.writtenAt;
        }
      } catch {
        /* not JSON — treat as stale */
      }
      const isStale = writtenAt == null || now - writtenAt > maxAgeMs;
      if (isStale) {
        localStorage.removeItem(k);
        report.flagged += 1;
        report.repaired += 1;
        report.details.push({ kind: 'stale-cache', key: k, writtenAt });
      }
    } catch {
      /* ignore */
    }
  }
  return report;
}

// ─── Aggregate runner ──────────────────────────────────────────────────────
export async function runAllReconciliation(
  ownerId: string,
  apply = false,
): Promise<ReconciliationReport[]> {
  const reports: ReconciliationReport[] = [];
  reports.push(await reconcileOneSidedSpouse(ownerId, apply));
  reports.push(await reconcileDuplicateMembers(ownerId));
  reports.push(await reconcileOrphanedParents(ownerId));
  // Orphan in-law spouse repair — pulls a stranded "Son-in-law / Daughter-
  // in-law / spouse" row out of Needs Connection by:
  //   1. detecting same-normalized-name duplicates within the orphan set
  //      and merging them into the oldest survivor (preserves photos,
  //      memories, comments, descendants' parent_ids, and any incoming
  //      spouse_id pointers).
  //   2. picking the most-likely spouse anchor — a structural descendant
  //      of the viewer who shares ≥1 ≥2-char name token with the orphan
  //      (handles "Terrance Lewis Sr." ↔ "Chekesha Graham-Lewis" via the
  //      shared "lewis" token, and any analogous future orphan).
  //   3. writing a reciprocal spouse_id pair (anchor's side only when
  //      currently null OR pointing at one of the deleted duplicates —
  //      existing reciprocal couples with a third party are preserved).
  //   4. setting relationship_override to a sensible in-law label
  //      ("Son-in-law" by default, "Daughter-in-law" / etc. when the
  //      orphan's existing override already specifies it).
  // Implemented in `src/lib/orphanInLawRepair.ts`. The helper is permanently
  // observation-only. apply=true records governed repair demand but never
  // executes a Tree, media, ancestry, spouse, override, or deletion write.
  try {
    const { reconcileOrphanInLawSpouses } = await import('./orphanInLawRepair');
    reports.push(await reconcileOrphanInLawSpouses(ownerId, apply));
  } catch (importErr) {
    // Best-effort. A failure to import the helper must never block the
    // rest of the reconciliation pipeline.
    // eslint-disable-next-line no-console
    console.warn('[reconciliation] orphan-in-law import failed', importErr);
  }
  reports.push(reconcileStaleCaches());
  // Expose latest run for diagnostics.
  if (typeof window !== 'undefined') {
    (window as unknown as Record<string, unknown>).__kcReconcile = {
      lastRun: new Date().toISOString(),
      reports,
    };
  }
  return reports;
}
